/**
 * V2 admin · Room detail.
 *
 * Tabs: Current tenant · History · Lessor & Bank · Documents · Notes
 * per V2 handoff §4.3.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BedDouble, EyeOff, KeyRound, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DocumentsPanel } from "@/components/admin/v2/documents/DocumentsPanel";
import { NotesPanel } from "@/components/admin/v2/notes/NotesPanel";
import { useOpenNoteCount } from "@/hooks/v2/useEntityNotes";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLessor } from "@/hooks/v2/useLessors";
import { useLessorIdByOwnerUser } from "@/hooks/v2/owner-user-lessor";
import {
  resolveEffectiveBank,
  resolveEffectiveKeyPickup,
  resolveEffectiveRep,
  roomOccupancyPhase,
  useApartment,
  useBookingsForRoom,
  useDeleteRoom,
  useProperty,
  usePropertyBankOverride,
  useRoom,
  useRoomBankOverride,
  useUpdateRoom,
} from "@/hooks/v2/useProperties";
import { useRoomHistory } from "@/hooks/v2/useRoomHistory";
import { RoomEditSheet } from "@/components/admin/v2/RoomEditSheet";
import { DetailActionsMenu } from "@/components/admin/v2/DetailActionsMenu";
import { DeleteConfirmDialog } from "@/components/admin/v2/DeleteConfirmDialog";
import { DetailPhotoGallery } from "@/components/admin/v2/DetailPhotoGallery";
import { CurrentTenantTab } from "@/components/admin/v2/room-tabs/CurrentTenantTab";
import { HistoryTab } from "@/components/admin/v2/room-tabs/HistoryTab";
import { RoomLessorBankTab } from "@/components/admin/v2/room-tabs/RoomLessorBankTab";
import { pickEffectiveFloor } from "@/lib/roomFloor";
import { formatEUR } from "@/lib/v2-format";
import { extractImageUrls } from "@/lib/v2-images";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useOwnerPropertyAccess } from "@/hooks/v2/useOwnerPropertyAccess";

export default function V2RoomDetail() {
  const { id, roomId } = useParams<{ id: string; roomId: string }>();
  const propertyId = id ? Number(id) : null;
  const roomIdNum = roomId ? Number(roomId) : null;
  const { isOwnerPortalOnly } = useAuth();
  const { data: ownerAllowed, isFetched: ownerAccessFetched } =
    useOwnerPropertyAccess(isOwnerPortalOnly ? propertyId : null);

  const { data: room, isLoading: rLoading, error: rError } = useRoom(roomIdNum);
  const { data: property } = useProperty(propertyId);
  // At page level, not inside the tab: Radix unmounts inactive tabs, so a count
  // living in the panel would only appear once you were already looking at it.
  const openNoteCount = useOpenNoteCount(
    roomIdNum === null ? null : { level: "room", roomId: roomIdNum }
  );
  // Apartment this room belongs to, so the breadcrumb can offer a "back to
  // apartment" step (COL-265).
  const { data: apartment } = useApartment(room?.apartment_id ?? null);
  const { data: lessor } = useLessor(property?.lessor_id ?? null);
  // The owner the room's APARTMENT holds itself — its direct lessor_id, else the
  // lessor behind its owner ACCOUNT. No property fallback: this feeds the
  // representor chain, where an apartment that owns nothing has to keep falling
  // through to the property's own override. See resolveEffectiveRep.
  const { data: lessorByOwnerUser = {} } = useLessorIdByOwnerUser();
  const apartmentOwnLessorId =
    apartment?.lessor_id ??
    (apartment?.owner_user_id
      ? (lessorByOwnerUser[apartment.owner_user_id] ?? null)
      : null);
  const { data: apartmentLessor } = useLessor(apartmentOwnLessorId);
  const { data: propertyBank } = usePropertyBankOverride(propertyId);
  const { data: roomBank } = useRoomBankOverride(roomIdNum);
  const { data: bookings = [] } = useBookingsForRoom(roomIdNum);
  // Same lists the History tab renders, so the tab-bar count matches the tab.
  const roomHistory = useRoomHistory(bookings);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const updateMut = useUpdateRoom();
  const deleteMut = useDeleteRoom();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, language } = useLanguage();

  useEffect(() => {
    if (!isOwnerPortalOnly || !propertyId) return;
    if (!ownerAccessFetched) return;
    if (ownerAllowed === false) {
      navigate("/admin/properties", { replace: true });
    }
  }, [
    isOwnerPortalOnly,
    propertyId,
    ownerAccessFetched,
    ownerAllowed,
    navigate,
  ]);

  // The whole chain, in create-contract's order: room override → the
  // apartment's rep_override → the apartment's own owner → property override →
  // property owner. The apartment links were missing, so a room in an apartment
  // with its own representor showed the PROPERTY's — while the contract this
  // room generates printed the apartment's. `apartments.rep_override` is new, so
  // without these two arguments it would have been invisible on this screen.
  const effectiveRep = useMemo(
    () =>
      resolveEffectiveRep({
        room: room ?? null,
        apartment: apartment ?? null,
        apartmentLessor: apartmentLessor ?? null,
        property: property ?? null,
        lessor: lessor ?? null,
      }),
    [room, apartment, apartmentLessor, property, lessor]
  );
  const effectiveBank = useMemo(
    () =>
      resolveEffectiveBank({
        roomBank: roomBank ?? null,
        propertyBank: propertyBank ?? null,
        lessor: lessor ?? null,
      }),
    [roomBank, propertyBank, lessor]
  );

  if (rLoading) {
    return (
      <AdminV2Layout
        title={t("admin.state.loading", "Loading…")}
        breadcrumbs={[
          { label: t("admin.nav.admin", "Admin"), to: "/admin" },
          {
            label: t("admin.nav.properties", "Properties"),
            to: "/admin/properties",
          },
          { label: t("admin.state.loading", "Loading…") },
        ]}
      >
        <Skeleton className="h-96 w-full" />
      </AdminV2Layout>
    );
  }

  if (rError || !room) {
    return (
      <AdminV2Layout
        title={t("admin.error.roomNotFound", "Room not found")}
        breadcrumbs={[
          { label: t("admin.nav.admin", "Admin"), to: "/admin" },
          {
            label: t("admin.nav.properties", "Properties"),
            to: "/admin/properties",
          },
          { label: t("admin.error.roomNotFound", "Room not found") },
        ]}
      >
        <Card className="p-8 space-y-3 max-w-lg">
          <h2 className="text-lg font-semibold">
            {t("admin.error.roomNotFound", "Room not found")} #{roomId}
          </h2>
          <p className="text-sm text-muted-foreground">
            {rError instanceof Error
              ? rError.message
              : t(
                  "admin.rooms.roomMayBeDeleted",
                  "This room may have been deleted."
                )}
          </p>
          {propertyId && (
            <Button asChild variant="outline" size="sm">
              <Link to={`/admin/properties/${propertyId}`}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("admin.apartments.backToProperty", "Back to property")}
              </Link>
            </Button>
          )}
        </Card>
      </AdminV2Layout>
    );
  }

  const cover = extractImageUrls(room.images)[0];
  // Occupancy is derived from bookings (single source of truth), not the
  // stale room.status/available fields: someone living there now, a committed
  // future tenant, or a tenant in their notice period who hasn't left yet.
  // Manual "maintenance" is the only status not represented by a booking, so
  // honor it when there's no tenant.
  const hasActiveBooking = bookings.some(
    (b) => roomOccupancyPhase(b) !== "ended"
  );
  const status: "occupied" | "vacant" | "maintenance" = hasActiveBooking
    ? "occupied"
    : room.status === "maintenance"
      ? "maintenance"
      : "vacant";

  // Same resolver the tenant and Edit room surfaces use, so the sidebar shows
  // what the tenant will read. The KeyNest codes stay per room (no inheritance).
  const keyPickup = resolveEffectiveKeyPickup({ room, apartment, property });
  const effectiveFloor = pickEffectiveFloor(room.floor, apartment?.floor);
  const isKeynest = keyPickup.method === "keynest";
  // Same room-then-property fallback the tenant sees. The link and codes open
  // the key box, and an owner-portal session only proves building access, not
  // ownership of this unit, so they stay staff-only.
  const accessUrl = room.keynest_access_url ?? property?.keynest_access_url;
  const showKeySecrets = isKeynest && !isOwnerPortalOnly;
  const keyPickupRows: { label: string; value: React.ReactNode }[] = [
    {
      // admin.field.pickupMethod has no German row (two call sites, two English
      // texts), so this label is language-aware inline instead.
      label: language === "de" ? "Abholart" : "Method",
      value:
        keyPickup.method === "manual"
          ? t("admin.field.pickupManual", "Manual address")
          : t("admin.field.pickupKeynest", "KeyNest"),
    },
    {
      label: t("admin.field.pickupLocation", "Pickup location"),
      value: keyPickup.location,
    },
    {
      label: t("admin.field.pickupAddress", "Pickup address"),
      value: keyPickup.address,
    },
    {
      label: t("admin.apartment.pickupInstructions", "Pickup instructions"),
      value: keyPickup.instructions,
    },
    ...(showKeySecrets
      ? [
          {
            label: t("admin.field.keynestAccessUrl", "KeyNest access link"),
            value: accessUrl ? (
              <a
                href={accessUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {accessUrl}
              </a>
            ) : null,
          },
          {
            label: t("admin.field.keynestPickupCode", "Pickup code"),
            value: room.keynest_pickup_code,
          },
          {
            label: t("admin.field.keynestAccessCode", "Access code"),
            value: room.keynest_access_code,
          },
        ]
      : []),
    ...(isKeynest
      ? [
          {
            label: t(
              "admin.field.keynestOpeningHoursWeekdays",
              "Opening hours (weekdays)"
            ),
            value: room.keynest_opening_hours_weekdays,
          },
          {
            label: t(
              "admin.field.keynestOpeningHoursSaturday",
              "Opening hours (Saturday)"
            ),
            value: room.keynest_opening_hours_saturday,
          },
          {
            label: t("admin.field.keynestKeyLabel", "Key label"),
            value: room.keynest_key_label,
          },
        ]
      : []),
  ].filter((r) => r.value);

  function handleToggleHide() {
    if (!room) return;
    updateMut.mutate(
      {
        id: room.id,
        propertyId: room.property_id,
        patch: { is_hidden: !room.is_hidden },
      },
      {
        onSuccess: () =>
          toast({
            title: room.is_hidden
              ? t("admin.rooms.roomUnhidden", "Room unhidden")
              : t("admin.rooms.roomHidden", "Room hidden"),
            description: room.is_hidden
              ? t(
                  "admin.rooms.bookableAgain",
                  "Bookable on the public listing again."
                )
              : t(
                  "admin.rooms.hiddenFromListing",
                  "Hidden from the public listing."
                ),
          }),
        onError: (err) =>
          toast({
            title: t(
              "admin.rooms.couldNotToggleVisibility",
              "Could not toggle visibility"
            ),
            description:
              err instanceof Error
                ? err.message
                : t("common.unknownError", "Unknown error"),
            variant: "destructive",
          }),
      }
    );
  }

  function handleDelete() {
    deleteMut.mutate(
      { id: room.id, propertyId: room.property_id },
      {
        onSuccess: () => {
          toast({
            title: t("admin.rooms.roomDeleted", "Room deleted"),
            description: room.title,
          });
          navigate(`/admin/properties/${room.property_id}`);
        },
        onError: (err) =>
          toast({
            title: t("admin.rooms.couldNotDeleteRoom", "Could not delete room"),
            description:
              err instanceof Error
                ? err.message
                : t("common.unknownError", "Unknown error"),
            variant: "destructive",
          }),
      }
    );
  }

  return (
    <AdminV2Layout
      title={room.title}
      titleBadge={
        room.is_hidden ? (
          <Badge variant="secondary" className="gap-1">
            <EyeOff className="h-3 w-3" />
            {t("admin.badge.hidden", "Hidden")}
          </Badge>
        ) : undefined
      }
      breadcrumbs={[
        { label: t("admin.nav.admin", "Admin"), to: "/admin" },
        {
          label: t("admin.nav.properties", "Properties"),
          to: "/admin/properties",
        },
        property
          ? { label: property.title, to: `/admin/properties/${property.id}` }
          : {
              label: `${t("admin.properties.propertyNoun", "Property")} #${id ?? "—"}`,
            },
        // Apartment step — only when the room is assigned to an apartment, so
        // the user can navigate back up to it (COL-265).
        ...(apartment && property
          ? [
              {
                label: apartment.label,
                to: `/admin/properties/${property.id}/apartments/${apartment.id}`,
              },
            ]
          : []),
        { label: room.title },
      ]}
      actions={
        <>
          <Button size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {t("common.edit", "Edit")}
          </Button>
          <DetailActionsMenu
            isHidden={!!room.is_hidden}
            onToggleHide={handleToggleHide}
            onDelete={() => setDeleteOpen(true)}
            disabled={updateMut.isPending || deleteMut.isPending}
          />
        </>
      }
      sidebar={
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <div className="aspect-[4/3] rounded-md overflow-hidden bg-muted">
              {cover ? (
                <img
                  src={cover}
                  alt={room.title}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <BedDouble className="h-8 w-8 text-muted-foreground/40" />
                </div>
              )}
            </div>
            <div className="space-y-1">
              <h3 className="text-sm font-semibold">{room.title}</h3>
              {property && (
                <Link
                  to={`/admin/properties/${property.id}`}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {property.title}
                </Link>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Badge
                variant={status === "occupied" ? "default" : "outline"}
                className="text-xs font-normal capitalize"
              >
                {status === "occupied"
                  ? t("admin.field.occupied", "Occupied")
                  : status === "maintenance"
                    ? t("admin.status.maintenance", "Maintenance")
                    : t("admin.status.vacant", "Vacant")}
              </Badge>
              {room.is_hidden && (
                <Badge variant="secondary" className="gap-1 text-xs">
                  <EyeOff className="h-3 w-3" /> {t("common.hidden", "Hidden")}
                </Badge>
              )}
            </div>
            <dl className="space-y-1.5 text-xs pt-2 border-t">
              <Row
                label={t("admin.field.rentMonthly", "Price")}
                value={`${formatEUR(room.price) ?? "—"}/${t("admin.rooms.perMonthSuffix", "mo")}`}
              />
              {(room as { deposit_amount?: number | null }).deposit_amount !=
                null && (
                <Row
                  label={t("admin.field.depositEuro", "Deposit")}
                  value={
                    formatEUR(
                      (room as { deposit_amount?: number | null })
                        .deposit_amount
                    ) ?? "—"
                  }
                />
              )}
              {room.size_sqm && (
                <Row
                  label={t("admin.field.size", "Size")}
                  value={`${room.size_sqm} m²`}
                />
              )}
              {effectiveFloor && (
                <Row
                  label={t("admin.field.floor", "Floor")}
                  value={
                    room.floor?.trim()
                      ? effectiveFloor
                      : `${effectiveFloor} ${language === "de" ? "(von Wohnung)" : "(from apartment)"}`
                  }
                />
              )}
              {room.available_from && (
                <Row
                  label={t("admin.field.availableFrom", "Available from")}
                  value={room.available_from}
                />
              )}
              {room.roommates_count != null && (
                <Row
                  label={t("admin.field.roommates", "Roommates")}
                  value={String(room.roommates_count)}
                />
              )}
            </dl>
          </Card>
          <Card className="p-4 space-y-2">
            <header className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <KeyRound className="h-3.5 w-3.5" />
              {t("admin.property.section.keyPickup", "Key pickup")}
            </header>
            <dl className="space-y-1.5 text-xs">
              {keyPickupRows.map((r) => (
                <Row key={r.label} label={r.label} value={r.value} />
              ))}
            </dl>
          </Card>
        </div>
      }
    >
      {/* Room photo gallery — read-only. The sidebar's small cover thumb
          gives the at-a-glance view; this gallery is the full set with
          a clickable lightbox. Tighter aspect ratio than property
          galleries since rooms tend to be photographed closer-up. */}
      <div className="mb-6">
        <DetailPhotoGallery
          images={room.images}
          title={room.title}
          aspect="4/3"
          emptyAction={
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {t("admin.rooms.uploadPhotos", "Upload photos")}
            </Button>
          }
        />
      </div>

      {/* Description — room-level marketing copy. Hidden when not set. */}
      {room.description && room.description.trim().length > 0 && (
        <Card className="p-5 mb-6 space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t("admin.rooms.aboutThisRoom", "About this room")}
          </h3>
          <p className="text-sm leading-relaxed whitespace-pre-line">
            {room.description}
          </p>
        </Card>
      )}

      <Tabs defaultValue="current" className="space-y-4">
        <TabsList>
          <TabsTrigger value="current">
            {t("admin.tab.currentTenant", "Current tenant")}
          </TabsTrigger>
          <TabsTrigger value="history">
            {t("admin.tab.history", "History")}
            {!roomHistory.resolving && !roomHistory.failed && (
              <span className="ml-1.5 text-xs opacity-60 tabular-nums">
                (
                {roomHistory.upcoming.length +
                  roomHistory.past.length +
                  roomHistory.applications.length}
                )
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="lessor-bank">
            {t("admin.tab.lessorAndBank", "Lessor & bank")}
          </TabsTrigger>
          <TabsTrigger value="documents">
            {t("admin.tab.documents", "Documents")}
          </TabsTrigger>
          <TabsTrigger value="notes" className="relative">
            {t("admin.tab.notes", "Notes")}
            {openNoteCount > 0 && (
              <span
                className="absolute -right-2 -top-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full border-2 border-background bg-petrol px-1 text-[9px] font-bold leading-none text-white shadow-sm"
                title={`${openNoteCount} ${t("admin.rooms.openNotes", "open {{count}}").replace("{{count}}", openNoteCount === 1 ? t("admin.rooms.noteSingular", "note") : t("admin.rooms.noteNounPlural", "notes"))}`}
                aria-label={`${openNoteCount} ${t("admin.rooms.openNotes", "open {{count}}").replace("{{count}}", t("admin.rooms.noteNounPlural", "notes"))}`}
              >
                {openNoteCount > 99 ? "99+" : openNoteCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current">
          <CurrentTenantTab
            propertyId={propertyId!}
            roomId={room.id}
            bookings={bookings}
          />
        </TabsContent>

        <TabsContent value="history">
          <HistoryTab
            propertyId={propertyId!}
            roomId={room.id}
            bookings={bookings}
          />
        </TabsContent>

        <TabsContent value="lessor-bank">
          <RoomLessorBankTab
            effectiveRep={effectiveRep}
            effectiveBank={effectiveBank}
            onEdit={() => setEditOpen(true)}
          />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsPanel
            scope="room"
            propertyId={room.property_id}
            roomId={room.id}
            source="admin"
            description={t(
              "admin.rooms.documentsPanelDesc",
              "Files attached to this room — handover protocols, photos, anything tied to the room rather than to the unit."
            )}
          />
        </TabsContent>

        <TabsContent value="notes">
          <NotesPanel
            scope={{ level: "room", roomId: room.id }}
            title={t("admin.tab.notes", "Notes")}
            emptyHint={t(
              "admin.notes.roomHint",
              "Anything the next admin should know about this room — key handover quirks, damage history, what the last tenant left behind."
            )}
          />
        </TabsContent>
      </Tabs>

      <RoomEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        room={room}
        bankOverride={roomBank ?? null}
      />

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        entityLabel={t("admin.rooms.roomNoun", "room")}
        entityName={room.title}
        actionLabel={t("admin.rooms.deleteRoom", "Delete room")}
        isDeleting={deleteMut.isPending}
        onConfirm={handleDelete}
        description={
          bookings.length > 0 ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 space-y-1">
              <p className="font-medium text-foreground">
                {bookings.length === 1
                  ? t(
                      "admin.rooms.oneBookingLinked",
                      "1 booking is linked to this room."
                    )
                  : t(
                      "admin.rooms.bookingsLinked",
                      "{{count}} bookings are linked to this room."
                    ).replace("{{count}}", String(bookings.length))}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  "admin.rooms.bookingsKeepDataHint",
                  "The bookings keep their data but lose their room reference. Review tenant detail before proceeding if any are active."
                )}
              </p>
            </div>
          ) : (
            <p className="text-muted-foreground">
              {t("admin.field.noBookingsLinked", "No bookings are linked.")}
            </p>
          )
        }
        requireTypedConfirm={bookings.length > 0}
      />
    </AdminV2Layout>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="font-medium min-w-0 text-right break-words [overflow-wrap:anywhere]">
        {value}
      </dd>
    </div>
  );
}
